import { useCallback, useMemo, useState } from 'react'
import {
  type WhatsappTemplate,
  type WhatsappCampaignFormData,
  type RecipientFilters,
} from '../types'
import { type MergeTagOptions } from '@/lib/mergeTags'
import { defaultRecipientFilters } from '../../components/recipient_selection/types'
import {
  type HeaderImageType,
  type CustomImageMode,
  type SelectOptionGroup,
  buildSelectOptionGroups,
  detectParameterSlots,
  renderPreview,
  unmappedSlots,
} from './helpers'

export type { SelectOptionGroup }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WizardStep = 0 | 1

export interface WizardState {
  currentStep: WizardStep
  campaignName: string
  parameterValues: Record<string, string>
  headerImageType: HeaderImageType
  headerImageUrl: string
  uploadPreviewUrl: string | null
  customImageMode: CustomImageMode
  isUploading: boolean
  headerTextValue: string
  selectedRecipientIds: string[]
  recipientFilters: RecipientFilters
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseWizardStateArgs {
  template: WhatsappTemplate
  initialCampaign: WhatsappCampaignFormData
  initialRecipientFilters?: RecipientFilters
  mergeTagOptions: MergeTagOptions
}

export function useWizardState({
  template,
  initialCampaign,
  initialRecipientFilters,
  mergeTagOptions,
}: UseWizardStateArgs) {
  // Derive initial header image state from saved parameterValues
  const initialHeaderImageValue = (initialCampaign.parameterValues || {}).header_image ?? ''
  const initialHeaderImageType: HeaderImageType =
    initialHeaderImageValue === 'attendee.qr_code' || initialHeaderImageValue === ''
      ? 'attendee.qr_code'
      : 'custom_url'
  const initialHeaderImageUrl =
    initialHeaderImageType === 'custom_url' ? initialHeaderImageValue : ''

  const [state, setState] = useState<WizardState>({
    currentStep: 0,
    campaignName: initialCampaign.campaignName || template.name,
    parameterValues: initialCampaign.parameterValues || {},
    headerImageType: initialHeaderImageType,
    headerImageUrl: initialHeaderImageUrl,
    uploadPreviewUrl: null,
    customImageMode: 'upload',
    isUploading: false,
    headerTextValue: (initialCampaign.parameterValues || {}).header_text ?? '',
    selectedRecipientIds: initialCampaign.recipientIds || [],
    recipientFilters:
      initialRecipientFilters || initialCampaign.recipientFilters || defaultRecipientFilters,
  })

  const updateState = useCallback((partial: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...partial }))
  }, [])

  // Derived / memoised values
  const templateBody = useMemo(
    () => initialCampaign.templateBody || template.bodyText || '',
    [initialCampaign.templateBody, template.bodyText],
  )

  const allSlots = useMemo(() => detectParameterSlots(templateBody), [templateBody])

  const selectOptions: SelectOptionGroup[] = useMemo(
    () => buildSelectOptionGroups(mergeTagOptions),
    [mergeTagOptions],
  )

  const missingSlots = useMemo(
    () => unmappedSlots(allSlots, state.parameterValues),
    [allSlots, state.parameterValues],
  )

  const previewText = useMemo(
    () => renderPreview(templateBody, state.parameterValues, mergeTagOptions),
    [templateBody, state.parameterValues, mergeTagOptions],
  )

  const buildParameterValuesPayload = useCallback((): Record<string, string> => {
    const payload = { ...state.parameterValues }

    if (template.hasHeaderImage) {
      const headerImageValue =
        state.headerImageType === 'attendee.qr_code'
          ? 'attendee.qr_code'
          : state.headerImageUrl.trim()
      if (headerImageValue) {
        payload.header_image = headerImageValue
      }
    }

    if (template.hasHeaderText && template.headerText) {
      if (/\{\{1\}\}/.test(template.headerText) && state.headerTextValue.trim()) {
        payload.header_text = state.headerTextValue.trim()
      }
    }

    return payload
  }, [state.parameterValues, state.headerImageType, state.headerImageUrl, state.headerTextValue, template])

  return {
    state,
    updateState,
    templateBody,
    allSlots,
    missingSlots,
    selectOptions,
    previewText,
    buildParameterValuesPayload,
  }
}
